import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import ProductService from "@/services/product";
import { useCart } from "contexts/cart";
import { useApp } from "contexts/app";
import { useStore } from "contexts/store";
import { useAuth } from "contexts/auth";
import { useChat } from "contexts/chat";

const useAddToCartViewModel = () => {
    const {
        processing,
        setProcessing,
        openAddToCart,
        setOpenAddToCart,
        addToCartProductID,
        addToCartVariantID,
        showToast,
        showProductSearch,
        setShowProductSearch,
    } = useApp();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [selectedVariant, setSelectedVariant] = useState();
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [itemQuantity, setItemQuantity] = useState(1);
    const [productOptions, setProductOptions] = useState([]);
    const [images, setImages] = useState();
    const [headingTitle, setHeadingTitle] = useState("");
    const [itemCaculatedPrice, setItemCaculatedPrice] = useState(0);
    const [variantImage, setVariantImage] = useState({});
    const [lineItems, setLineItems] = useState([]);
    const [showAddMultiVariant, setShowAddMultiVariant] = useState(false);

    const service = new ProductService();
    const navigate = useNavigate();
    const { addToCart, calculateItemPrice, addMultiItemToCart } = useCart();
    const { store } = useStore();
    const { openChat, hasChatEntry } = useChat();
    const { user } = useAuth();

    const id = addToCartProductID;
    const showLogin = !!user || !!store?.web?.public_price_display;

    const toID = (value) => String(value ?? "");

    const getFirstValidImage = (imageList = []) => {
        if (!Array.isArray(imageList)) {
            return null;
        }

        for (let index = 0; index < imageList.length; index++) {
            const image = imageList[index];
            if (typeof image === "string" && /^https?:\/\//i.test(image)) {
                return { url: image };
            }
            if (image?.url) {
                return image;
            }
        }

        return null;
    };

    const getVariantImages = (variant) => {
        if (!variant) {
            return [];
        }

        if (Array.isArray(variant.images) && variant.images.length > 0) {
            return variant.images;
        }

        if (variant.image) {
            return [variant.image];
        }

        return [];
    };

    const getImageSrc = (image) => image?.lg_url || image?.md_url || image?.sm_url || image?.url || "";

    const findVariantBySelectedOptions = (nextSelectedOptions = []) => {
        const pairs = (productOptions || []).map((option, index) => ({
            optionID: toID(option?.id),
            valueID: toID(nextSelectedOptions[index]?.id),
        }));

        if (!pairs.length || pairs.some((item) => !item.optionID || !item.valueID)) {
            return null;
        }

        return (data?.variants || []).find((variant) => {
            const variantOptions = Array.isArray(variant?.options) ? variant.options : [];
            return pairs.every((pair) => {
                const matched = variantOptions.find((option) => toID(option?.id) === pair.optionID);
                return toID(matched?.value) === pair.valueID;
            });
        }) || null;
    };

    const createLineItems = () => {
        const variants = data?.variants ?? [];
        const lst = variants.map((element) => {
            const isVariantOutOfStock = !!data.track_inventory && !data.allow_backorder && (element.available_quantity ?? 0) <= 0;
            return {
                product_id: element.id,
                variant_title: element.variant_title,
                quantity: 0,
                price: 0,
                out_of_stock: isVariantOutOfStock,
                available_quantity: element.available_quantity ?? 0,
            };
        });
        setLineItems(lst);
    };

    const calculateSelectedItemPrice = async () => {
        const price = await calculateItemPrice(selectedVariant, itemQuantity);
        setItemCaculatedPrice(price);
    };

    const load = async () => {
        if (!id) {
            return;
        }

        setLoading(true);
        const response = await service.get(id);
        setData(response);
        setProductOptions(response?.options ?? []);
        setSelectedOptions((response?.options || []).map(() => null));
        setItemQuantity(1);
        setSelectedVariant(null);

        let imageList = [];
        const productImage = (response?.images || []).map((x) => ({ ...x, image_of: "product" }));
        imageList = imageList.concat(productImage);

        for (let index = 0; index < (response?.variants || []).length; index++) {
            const variant = response.variants[index];
            const variantImages = getVariantImages(variant);
            const variantImageList = variantImages.map((x) => ({ ...x, image_of: "variant", variant_id: variant.variant_id, variant }));
            imageList = imageList.concat(variantImageList);
        }

        if ((response?.variants || []).length === 1) {
            setSelectedVariant(response.variants[0]);
        }

        setImages(imageList);
        setHeadingTitle(response?.name || "");
        setLoading(false);
    };

    const onSelectOption = (option, index) => {
        const newSelect = [...selectedOptions];
        newSelect[index] = option;
        setSelectedOptions(newSelect);

        const variant = findVariantBySelectedOptions(newSelect);
        if (variant) {
            setSelectedVariant(variant);
            if (data.track_inventory && !data.allow_backorder) {
                const maxQty = variant.available_quantity ?? 0;
                if (maxQty > 0 && itemQuantity > maxQty) {
                    setItemQuantity(maxQty);
                }
            }
            return;
        }

        setSelectedVariant();
    };

    const getInventoryErrorDescription = (error, options = {}) => {
        const { isMulti = false } = options;
        const errors = Array.isArray(error?.inventory_errors) ? error.inventory_errors : [];
        if (errors.length === 0) {
            return "Số lượng vượt quá tồn kho hiện tại";
        }

        const toVariantTitle = (variantID) => {
            const variant = (data?.variants ?? []).find((x) => x.id == variantID);
            return variant?.variant_title || variantID || "Phân loại";
        };

        const messages = errors.map((item) => {
            const title = toVariantTitle(item?.variant_id);
            if (item?.reason === "variant_not_found") {
                return `${title}: không còn tồn tại hoặc đã ngừng bán`;
            }
            if (item?.reason === "insufficient_available") {
                const available = Number(item?.available ?? 0);
                if (available <= 0) {
                    return `${title}: hết hàng`;
                }
                return `${title}: chỉ còn ${available} sản phẩm có thể đặt`;
            }
            return `${title}: số lượng vượt quá tồn kho hiện tại`;
        });

        if (!isMulti || messages.length === 1) {
            return messages[0];
        }

        return `Một số phân loại không đủ tồn kho: ${messages.join("; ")}`;
    };

    const addMuiltiVariantToCart = async () => {
        const count = _.sumBy(lineItems, "quantity");

        if (count === 0) {
            showToast({
                description: "Bạn phải thêm một phân loại",
                type: "error",
            });
            return;
        }

        setProcessing(true);
        try {
            const lineItemsToAdd = lineItems.filter((x) => x.quantity > 0).map((x) => ({ product_id: x.product_id, quantity: x.quantity }));
            await addMultiItemToCart(lineItemsToAdd);
            setOpenAddToCart(false);
            showToast({
                description: `Đã thêm ${_.sumBy(lineItemsToAdd, "quantity")} sản phẩm vào giỏ hàng.`,
                type: "success",
                duration: 6000,
                action: {
                    label: "Xem giỏ hàng",
                    onClick: () => navigate("/cart"),
                },
            });
            if (showProductSearch) {
                setShowProductSearch(false);
            }
        } catch (error) {
            showToast({
                description: getInventoryErrorDescription(error, { isMulti: true }),
                type: "error",
                duration: 6000,
            });
        } finally {
            setProcessing(false);
        }
    };

    const add1VariantToCart = async () => {
        if (!selectedVariant) {
            showToast({
                description: "Bạn phải chọn một phân loại",
                type: "error",
            });
            return;
        }

        setProcessing(true);
        try {
            await addToCart(selectedVariant.id, itemQuantity);
            setOpenAddToCart(false);
            showToast({
                description: `Đã thêm ${itemQuantity} ${selectedVariant.variant_title}   vào giỏ hàng.`,
                type: "success",
                duration: 6000,
                action: {
                    label: "Xem giỏ hàng",
                    onClick: () => navigate("/cart"),
                },
            });
            if (showProductSearch) {
                setShowProductSearch(false);
            }
        } catch (error) {
            showToast({
                description: getInventoryErrorDescription(error),
                type: "error",
                duration: 6000,
            });
        } finally {
            setProcessing(false);
        }
    };

    const addToCartClick = async () => {
        if (showAddMultiVariant) {
            addMuiltiVariantToCart();
            return;
        }

        add1VariantToCart();
    };

    const onLineItemChangeQuantity = async (product_id, quantity) => {
        const nextLineItems = [...lineItems];
        const old = nextLineItems.find((x) => x.product_id == product_id);
        const variant = (data?.variants || []).find((x) => x.id == product_id);

        if (data.track_inventory && !data.allow_backorder && variant) {
            quantity = Math.min(quantity, variant.available_quantity ?? 0);
        }

        const price = await calculateItemPrice(variant, quantity);
        if (old) {
            old.quantity = quantity;
            old.price = price;
        } else {
            nextLineItems.push({ product_id, quantity, price });
        }

        setLineItems(nextLineItems);
    };

    const onItemQuantityClick = (quantity) => {
        if (!showAddMultiVariant) {
            if (data.track_inventory && !data.allow_backorder && selectedVariant) {
                quantity = Math.min(quantity, selectedVariant.available_quantity ?? 0);
            }
            setItemQuantity(quantity);
            return;
        }

        onLineItemChangeQuantity(selectedVariant.id, quantity);
    };

    const getStockHint = (availableQty, orderedQty) => {
        if (!data.track_inventory || data.allow_backorder) {
            return null;
        }

        const avail = availableQty ?? 0;
        if (avail <= 0) {
            return null;
        }

        const nearLimit = avail <= 10 || orderedQty >= Math.ceil(avail / 2);
        return nearLimit ? avail : null;
    };

    useEffect(() => {
        load();
    }, [id, user?.id]);

    useEffect(() => {
        createLineItems();
    }, [data]);

    useEffect(() => {
        if (selectedVariant) {
            setHeadingTitle(selectedVariant.variant_title);
            const image = getFirstValidImage(getVariantImages(selectedVariant)) || getFirstValidImage(data.images);
            setVariantImage(image || null);
            return;
        }

        setHeadingTitle(data?.name || "");
        setVariantImage(getFirstValidImage(data.images) || null);
    }, [selectedVariant, data]);

    useEffect(() => {
        calculateSelectedItemPrice();
    }, [selectedVariant, itemQuantity]);

    useEffect(() => {
        if (!openAddToCart) {
            return;
        }

        if (!Array.isArray(data?.variants)) {
            return;
        }

        if (!addToCartVariantID) {
            setSelectedVariant(null);
            setSelectedOptions((data?.options || []).map(() => null));
            setItemQuantity(1);
            return;
        }

        const variant = data.variants.find((x) => x.id == addToCartVariantID);
        if (variant) {
            setSelectedVariant({ ...variant });
            setSelectedOptions((variant.options || []).map((x) => ({ id: x.value })));
            setItemQuantity(1);
            return;
        }

        setSelectedVariant(null);
        setSelectedOptions((data?.options || []).map(() => null));
        setItemQuantity(1);
    }, [openAddToCart, addToCartVariantID, data?.variants, data?.options]);

    return {
        processing,
        openAddToCart,
        setOpenAddToCart,
        loading,
        data,
        selectedVariant,
        selectedOptions,
        itemQuantity,
        setItemQuantity,
        productOptions,
        images,
        headingTitle,
        itemCaculatedPrice,
        variantImage,
        lineItems,
        showAddMultiVariant,
        setShowAddMultiVariant,
        showLogin,
        hasChatEntry,
        openChat,
        onSelectOption,
        addToCartClick,
        onLineItemChangeQuantity,
        onItemQuantityClick,
        getStockHint,
        getImageSrc,
    };
};

export default useAddToCartViewModel;
